import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { Fragment } from 'react';

import Text from '~/components/Text';

import type { Publication } from '~/interfaces/publication';

import { Image } from '../Image';

interface Props {
  title: string;
  description: string | React.ReactElement<any> | number | string;
  isOpen: boolean;
  onClose: () => void;
  children?: React.ReactElement<any> | number | string;
  publication: Publication;
  alternativeAction?: React.ReactElement<any> | number | string;
}

const SubscribeLoginSlideUpModal = ({ isOpen, onClose, children, title, publication, alternativeAction }: Props) => {
  const showLoginWithPassword = !!publication?.has_subscription_password;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-50"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-50"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bottom-0 left-0 z-20 bg-black/60" />
        </Transition.Child>

        <Transition.Child
          as={Fragment}
          enter="ease-out duration-100"
          enterFrom="transform sm:translate-y-0 translate-y-1/2 opacity-0"
          enterTo="transform translate-y-0 opacity-100"
          leave="ease-in duration-100"
          leaveFrom="transform translate-y-0 opacity-100"
          leaveTo="transform sm:translate-y-0 translate-y-1/2 opacity-0"
        >
          <div className="fixed inset-0 bottom-0 left-0 z-40 flex items-end overflow-y-auto sm:items-center">
            <Dialog.Panel className="relative flex justify-center w-full mx-auto overflow-auto text-left align-middle transition-all transform max-w-none rounded-t-wt bg-wt-background sm:max-w-xl sm:rounded-wt">
              <div className="w-full">
                <div className="relative p-6">
                  <div className="absolute right-6">
                    <button
                      aria-label="Close"
                      className="flex items-center justify-center w-6 h-6 transition-all rounded-wt hover:bg-black/5"
                      onClick={onClose}
                    >
                      <XMarkIcon className="w-5 h-5 text-gray-400 transition-all stroke-1 hover:text-gray-700" />
                    </button>
                  </div>
                  <div className="">
                    <div className="flex flex-col">
                      <div className="w-12 h-12 mx-auto overflow-hidden rounded-wt">
                        <Image
                          className="object-contain w-full h-full rounded-wt"
                          src={publication?.logo?.thumb?.url || ''}
                          alt={`${title} logo`}
                          aspectRatio="square"
                          maxWidth={200}
                        />
                      </div>
                      {showLoginWithPassword ? (
                        <Text
                          as="h2"
                          size="xl"
                          type="title"
                          weight="medium"
                          theme={'default'}
                          className="mt-6 text-center break-words"
                        >
                          {title}
                        </Text>
                      ) : (
                        <>
                          <Text as="h4" size="2xl" theme="content" className="mt-6 text-center">
                            {title}
                          </Text>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-6">{children}</div>
                </div>
                {alternativeAction && <div className="p-6 text-center border-t">{alternativeAction}</div>}
              </div>
            </Dialog.Panel>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};

export default SubscribeLoginSlideUpModal;
